import { exportPortfolioStatementPDF } from '@/helpers/portfolioStatementHelper';
import { PDFViewer } from '@react-pdf/renderer';
import React, { useState } from 'react';
import { BsFileEarmarkPdf } from 'react-icons/bs';
import { Button } from '../common/Button';
import Divider from '../common/Divider';
import Loader from '../common/Loader';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from '../modal/Modal';
import PortfolioStatement from '../pdf/PortfolioStatement';

export interface StatementData {
    metadata: {
        date: string;
        entity: string;
        account: string;
        currency: string;
        balance: string;
    };
    positions: Array<{
        asset: string;
        total: string;
        rate: string;
        live_value: string;
        allocation: number;
    }>;
}

interface StatementExportModalProps extends ModalProps {
    statementData: StatementData;
}

const PortfolioStatementModal: React.FC<StatementExportModalProps> = (props: StatementExportModalProps) => {
    const { handlers, statementData } = props;
    const [isExporting, setIsExporting] = useState(false);

    const handleExportToPDF = async () => {
        try {
            setIsExporting(true);

            await exportPortfolioStatementPDF(statementData, new Date().toISOString().split('T')[0]);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            alert('Failed to export PDF. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    if (!statementData) return null;

    return (
        <Modal {...props} className="!h-full" size="max-w-xl">
            {/* bg-gradient-to-r from-brand-primary-dark to-brand-background  */}
            <ModalHeader className="px-2 py-4 text-brand-text">
                <ModalTitle className="px-3">
                    <div className="text-2xl font-bold">Export Statement</div>
                    <p className="text-sm text-brand-neutral-400 mt-1">Export your portfolio statement as a PDF</p>
                </ModalTitle>
                <ModalClose {...props} className="pr-2" onClick={handlers.close} />
            </ModalHeader>
            <Divider />
            {/* Modal Body */}
            <div className="flex flex-1 basis-0">
                {props.opened && (
                    <PDFViewer width="100%" height="100%" className="rounded-lg overflow-hidden p-6">
                        <PortfolioStatement {...statementData} />
                    </PDFViewer>
                )}
            </div>

            <Divider neutral />
            <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                <button
                    type="button"
                    className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                    onClick={handlers.close}
                >
                    Close
                </button>
                <Button
                    type="button"
                    onClick={handleExportToPDF}
                    disabled={isExporting}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isExporting ? (
                        <>
                            <Loader className="w-4 h-4" />
                            <span>Exporting...</span>
                        </>
                    ) : (
                        <>
                            <BsFileEarmarkPdf className="w-4 h-4" />
                            <span>Export as PDF</span>
                        </>
                    )}
                </Button>
            </div>
        </Modal>
    );
};

export default PortfolioStatementModal;
